<!--
  The link component, supporting different styles.
  NOTE: unlike other components, XLink doesn't provide a "size" option. The reason is we normally want to inherit the text size from the parent.
-->
<script setup lang="ts">
import InertiaLink from '@corp/components/InertiaLink'
import { isSameApp } from '@corp/helpers/url'
import type { ButtonHTMLAttributes } from 'vue'
import type { IconDefinition } from './XIcon.vue'

const {
  danger,
  disabled,
  download,
  href,
  icon,
  primary,
  secondary,
  target,
  type = 'button',
} = defineProps<{
  // Style variants for the link.
  primary?: boolean
  secondary?: boolean
  danger?: boolean

  // Does not respond to click events when disabled.
  disabled?: boolean

  // Causes the browser to treat the linked URL as a download.
  download?: boolean | string

  // Where to display the linked URL
  target?: '_blank'

  // The location this link should navigate to.
  href?: string

  // The name or font awesome path of an icon to render inside the button.
  icon?: false | IconDefinition

  // In case it is a button, the type of button, which affects the behavior when inside forms, etc.
  type?: ButtonHTMLAttributes['type']

  // Classes to pass to icon
  iconClass?: string
}>()

const tag = $computed(() => {
  if (!href) return 'button'
  if (download || target || !isSameApp(href) || String(href).startsWith('mailto:')) return 'a'
  return InertiaLink
})

const attrs = $computed(() => {
  if (tag === 'button') return { disabled, type }

  return {
    download: download === true ? '' : download === false ? undefined : '',
    href,
    target,
    class: { 'pointer-events-none': disabled },
  }
})

const variant = $computed(() => {
  if (primary || secondary || danger || icon)
    return ['link font-semibold', { primary, secondary, danger, 'with-icon': icon }]
})
</script>

<template>
  <component :is="tag" :class="variant" v-bind="attrs">
    <XIcon
      v-if="icon"
      size="inherit"
      :icon="icon"
      class="mr-2 color-dark-500"
      :class="iconClass"
    />
    <slot/>
  </component>
</template>

<style lang="scss" scoped>
button[disabled] {
  opacity: 0.2;
}

.icon {
  vertical-align: -0.15em;
}

.link {
  transition: color ease 0.3s;

  &.with-icon {
    height: 1em; // This is necessary for Icon to inherit size
  }

  &:focus {
    outline: none;
  }

  &.primary {
    color: theme("colors.blue.400");

    &:hover,
    &:focus {
      color: theme("colors.blue.300");
    }

    &:active {
      color: theme("colors.blue.500");
    }
  }

  &.secondary {
    color: theme("colors.dark.500");

    &:hover,
    &:focus {
      color: theme("colors.dark.300");
    }

    &:active {
      color: theme("colors.dark.500");
    }
  }

  &.danger {
    color: theme("colors.red.400");

    &:hover,
    &:focus {
      color: theme("colors.red.300");
    }

    &:active {
      color: theme("colors.red.500");
    }
  }
}
</style>
